import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  isOnlineMethod,
  type AddressView,
  type BillLine,
  type CustomerOrderDetail,
  type PaymentMethod,
  type Quote,
  type QuoteInput,
} from '@capsico/contracts';
import { errorMessage, errorStatus, errorRef } from '../api/client';
import { fetchQuote, placeOrder } from '../api/checkout';
import { track } from '../api/track';
import { cartInput, cartStore, cartSummary, useCart } from './useCart';
import { useAddresses } from './useAddresses';
import { useDeliveryPoint } from './useDeliveryPoint';
import { useFormDraft } from './useFormDraft';
import { useSession } from './useSession';
import { showToast } from './useToast';
import { clearOfferCode, pendingOfferCode } from './growthEntry';

/** The parts of checkout the customer types (saved as a draft; never anything secret). */
export interface CheckoutForm {
  kitchenNote: string;
  cutlery: boolean;
  forSomeoneElse: boolean;
  receiverName: string;
  receiverPhone: string;
  /** W14: no knock, no delivery code — the rider leaves it at the door and uploads a photo. */
  leaveAtDoor: boolean;
}

const EMPTY_FORM: CheckoutForm = {
  kitchenNote: '',
  cutlery: false,
  forSomeoneElse: false,
  receiverName: '',
  receiverPhone: '',
  leaveAtDoor: false,
};

const isEmptyForm = (form: CheckoutForm) =>
  !form.kitchenNote.trim() &&
  !form.cutlery &&
  !form.forSomeoneElse &&
  !form.receiverName.trim() &&
  !form.receiverPhone.trim() &&
  !form.leaveAtDoor;

/** Server error body with the fresh quote attached (409 price change, 422 blockers). */
function quoteFromError(error: unknown): Quote | null {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data as { error?: { details?: { quote?: Quote } } } | undefined;
  return body?.error?.details?.quote ?? null;
}

/**
 * Checkout state: the cart priced by the server (quote, refreshed as anything changes), the best
 * eligible offer applied automatically until the customer chooses otherwise, address, tip,
 * schedule, payment, kitchen note / cutlery / someone-else receiver (draft-saved), and placing
 * the order with one idempotency key per attempt. Components only render what this returns.
 */
export function useCheckout(messages: { pricesChanged: string; placed: string; deleted: string }) {
  const client = useQueryClient();
  const cart = useCart();
  const session = useSession();
  const point = useDeliveryPoint();
  const signedIn = session.signedIn;
  const saved = useAddresses(signedIn, { deleted: messages.deleted });

  /* W19: one checkout_start per cart restaurant while checkout is open (the server emits order_placed). */
  const checkoutTracked = useRef<string | null>(null);
  useEffect(() => {
    if (!cart || checkoutTracked.current === cart.outletId) return;
    checkoutTracked.current = cart.outletId;
    const summary = cartSummary(cart);
    track('checkout_start', {
      country: cart.country,
      outletId: cart.outletId,
      valueMinor: summary.estimateMinor,
      props: { items: summary.count },
    });
  }, [cart]);

  const [chosenAddressId, setChosenAddressId] = useState<string | null>(null);
  // A QR / share-link offer waits here until the next checkout.
  const [coupon, setCoupon] = useState<string | null>(() => pendingOfferCode());
  /** The customer picked or removed an offer: stop choosing one for them. */
  const [couponTouched, setCouponTouched] = useState(false);
  const [tipMinor, setTipMinor] = useState(0);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cod');
  /** Use Capsico Money first (on by default; the server applies at most the balance). */
  const [useWallet, setUseWallet] = useState(true);
  /** The last way to pay the customer picked themselves (restored when Capsico Money stops covering the bill). */
  const preferred = useRef<PaymentMethod | null>(null);
  const [form, setForm] = useState<CheckoutForm>(EMPTY_FORM);
  const [why, setWhy] = useState<BillLine | null>(null);
  const attemptKey = useRef<string | null>(null);

  const draft = useFormDraft(`checkout:${cart?.outletId ?? 'none'}`, form, {
    enabled: cart !== null,
    isEmpty: isEmptyForm,
    base: EMPTY_FORM,
  });

  const address: AddressView | null = useMemo(() => {
    const list = saved.addresses;
    if (list.length === 0) return null;
    return (
      list.find((entry) => entry.id === chosenAddressId) ??
      list.find((entry) => entry.id === point?.addressId) ??
      list.find((entry) => entry.isDefault) ??
      list[0] ??
      null
    );
  }, [saved.addresses, chosenAddressId, point?.addressId]);

  const where = address ? { lat: address.lat, lng: address.lng } : point;

  const input: QuoteInput | null = useMemo(() => {
    if (!cart || !where) return null;
    return {
      cart: cartInput(cart),
      lat: where.lat,
      lng: where.lng,
      addressId: address?.id ?? null,
      couponCode: coupon,
      tipMinor,
      scheduledFor,
      paymentMethod,
      useWallet,
    };
  }, [cart, where, address?.id, coupon, tipMinor, scheduledFor, paymentMethod, useWallet]);

  const inputKey = input ? JSON.stringify(input) : 'none';
  // Any change to what is being ordered starts a new attempt (a new idempotency key).
  useEffect(() => {
    attemptKey.current = null;
  }, [inputKey]);

  const quoteQuery = useQuery({
    queryKey: ['quote', inputKey],
    queryFn: () => fetchQuote(input as QuoteInput),
    enabled: input !== null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: 1,
  });
  const quote = quoteQuery.data ?? null;

  // Apply the best offer the customer is eligible for, until they choose for themselves.
  useEffect(() => {
    if (!quote || couponTouched) return;
    if (coupon && quote.couponError) {
      setCoupon(null);
      return;
    }
    if (!coupon) {
      const best = quote.offers.find((offer) => offer.eligible && offer.discountMinor > 0);
      if (best) setCoupon(best.code);
    }
  }, [quote, coupon, couponTouched]);

  // Keep the chosen payment method one the server offers.
  useEffect(() => {
    if (!quote) return;
    const offered = quote.payment.methods;
    const current = offered.find((method) => method.method === paymentMethod);
    if (current?.available) return;
    const again = offered.find((method) => method.method === preferred.current && method.available);
    const fallback = again ?? offered.find((method) => method.available);
    if (fallback && fallback.method !== paymentMethod) setPaymentMethod(fallback.method);
  }, [quote, paymentMethod]);

  // A scheduled slot the server no longer offers goes back to "now".
  useEffect(() => {
    if (quote && scheduledFor && !quote.schedule.slots.includes(scheduledFor)) {
      setScheduledFor(null);
    }
  }, [quote, scheduledFor]);

  const place = useMutation({
    mutationFn: async (): Promise<CustomerOrderDetail> => {
      if (!input || !quote || !address) throw new Error('Not ready');
      attemptKey.current ??= crypto.randomUUID();
      const someoneElse = form.forSomeoneElse;
      return placeOrder(
        {
          ...input,
          addressId: address.id,
          paymentMethod,
          kitchenNote: form.kitchenNote.trim() || null,
          cutlery: form.cutlery,
          receiverName: someoneElse ? form.receiverName.trim() || null : null,
          receiverPhone: someoneElse ? form.receiverPhone.trim() || null : null,
          expectedTotalMinor: quote.bill.totalMinor,
          leaveAtDoor: form.leaveAtDoor,
        },
        attemptKey.current,
      );
    },
    onSuccess: (order) => {
      attemptKey.current = null;
      draft.clear();
      clearOfferCode();
      cartStore.clear();
      client.setQueryData(['my-order', order.id], order);
      void client.invalidateQueries({ queryKey: ['my-orders'] });
      if (order.walletMinor > 0) void client.invalidateQueries({ queryKey: ['wallet'] });
      // Online orders wait for the gateway; "placed" is said once the money is in.
      if (order.stage !== 'awaiting_payment') showToast(messages.placed, { tone: 'positive' });
    },
    onError: (failure) => {
      const fresh = quoteFromError(failure);
      if (fresh) client.setQueryData(['quote', inputKey], fresh);
      if (errorStatus(failure) === 409) {
        attemptKey.current = null;
        showToast(messages.pricesChanged, { tone: 'negative' });
      } else {
        showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) });
      }
    },
  });

  const receiverPhoneOk =
    !form.forSomeoneElse || /^\+?[0-9]{7,15}$/.test(form.receiverPhone.replace(/\s/g, ''));
  const receiverOk =
    !form.forSomeoneElse || (form.receiverName.trim().length >= 2 && receiverPhoneOk);

  const canPlace =
    signedIn &&
    quote !== null &&
    quote.blockers.length === 0 &&
    address !== null &&
    receiverOk &&
    !quoteQuery.isFetching &&
    !place.isPending;

  return {
    cart,
    signedIn,
    sessionLoading: session.loading,
    point,
    /* quote */
    quote,
    quoteLoading: quoteQuery.isLoading,
    quoteRefreshing: quoteQuery.isFetching,
    quoteFailed: quoteQuery.isError && !quote,
    quoteError: quoteQuery.error ? errorMessage(quoteQuery.error) : null,
    retryQuote: () => void quoteQuery.refetch(),
    /* cart lines */
    setQuantity: (key: string, quantity: number) => cartStore.setQuantity(key, quantity),
    /* address */
    addresses: saved.addresses,
    addressesLoading: saved.loading,
    address,
    chooseAddress: (id: string) => setChosenAddressId(id),
    /* offers */
    coupon,
    applyCoupon: (code: string) => {
      setCouponTouched(true);
      setCoupon(code.trim().toUpperCase() || null);
    },
    removeCoupon: () => {
      setCouponTouched(true);
      setCoupon(null);
    },
    /* tip, schedule, payment */
    tipMinor,
    setTip: (minor: number) => setTipMinor(Math.max(0, minor)),
    scheduledFor,
    setScheduledFor,
    paymentMethod,
    setPaymentMethod: (method: PaymentMethod) => {
      if (method !== 'wallet') preferred.current = method;
      setPaymentMethod(method);
    },
    /* Capsico Money */
    useWallet,
    setUseWallet,
    /** What the pay bar says: online "Pay ₹X", Capsico Money only, or place (cash / test). */
    payKind: (paymentMethod === 'wallet'
      ? 'wallet'
      : isOnlineMethod(paymentMethod)
        ? 'online'
        : 'place') as 'wallet' | 'online' | 'place',
    /* form (draft-saved) */
    form,
    setField: <K extends keyof CheckoutForm>(field: K, value: CheckoutForm[K]) =>
      setForm((current) => ({ ...current, [field]: value })),
    receiverPhoneError: form.forSomeoneElse && form.receiverPhone.trim() !== '' && !receiverPhoneOk,
    draftRestored: draft.restored !== null,
    keepDraft: () => {
      const value = draft.keep();
      // Drafts saved before a field existed miss it: fill the gaps from the empty form.
      if (value) setForm({ ...EMPTY_FORM, ...value });
    },
    discardDraft: draft.discard,
    /* why-this-fee sheet */
    why,
    showWhy: setWhy,
    /* place */
    canPlace,
    placing: place.isPending,
    placedOrder: place.data ?? null,
    place: () => place.mutate(),
  };
}
